import React from 'react'
import { htmlDecode } from '../../../utils/html'
import { link, Link } from '../../../utils/link'
import { ImageSizesProps, ImageProps, ImageProportionsProps, ImageSourcesProps } from '../../modernBlocks.state'

type SVGIconProps = {
  iconId: string
  width?: string
  height?: string
  className?: string
}

export const SharedBlockLayout = {
  SVGIcon: (props: SVGIconProps) => {
    const { className, height, iconId, width } = props
    return (
      <svg className={className || ''} width={width || '20px'} height={height || '20px'} aria-hidden="true">
        <use xlinkHref={iconId} />
      </svg>
    )
  },
  Clickable: (props: {
    type: 'link' | 'button'
    link: Link
    children?: React.ReactNode
    classNames?: string
    id: string
    SVGIcon?: SVGIconProps
    onClick?: () => void
  }) => {
    const { link: linkProps, children, classNames, id, SVGIcon, type } = props
    const buttonInner = (
      <>
        {SVGIcon ? <SharedBlockLayout.SVGIcon className="button__icon" {...SVGIcon} /> : <></>}
        <span className="button__label">{htmlDecode(linkProps.title)}</span>
      </>
    )
    const linkInner = (
      <>
        {SVGIcon ? <SharedBlockLayout.SVGIcon className="link__icon" {...SVGIcon} /> : <></>}
        <span className="link__label">{htmlDecode(linkProps.title)}</span>
      </>
    )
    return link(
      linkProps,
      { classname: classNames || '' },
      <>{children ? children : type == 'button' ? buttonInner : linkInner}</>,
      id,
      props.onClick
    )
  },
  ModernImage: (props: {
    image: ImageProps
    imageProportions?: ImageProportionsProps
    lazyload: boolean
    priority: 'high' | 'low' | undefined
    classNames?: string
  }) => {
    const { classNames, image, imageProportions, lazyload, priority } = props
    return image ? (
      <img
        loading={lazyload == true ? "lazy" : "eager"}
        fetchpriority={priority}
        className={classNames}
        alt={image.altText}
        width={imageProportions && imageProportions.width ? imageProportions.width : undefined}
        height={imageProportions && imageProportions.height ? imageProportions.height : undefined}
        src={image.originalImageUrl}
      />
    ) : (
      <></>
    )
  },
  ModernImageSrcSet: (props: {
    image: ImageSizesProps['image']
    imageProportions: ImageProportionsProps
    lazyload: boolean
    priority: 'high' | 'low' | undefined
    classNames?: string
    pictureClassNames?: string
  }) => {
    const { classNames, pictureClassNames, image, imageProportions, lazyload, priority } = props
    return image ? (
      <picture className={pictureClassNames}>
        <source
          srcSet={image.imageSizes.length > 0
            ? image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
            : undefined
          }
          sizes={imageProportions.sizes ? imageProportions.sizes : '100vw'}
          type='image/webp'
        />
        <img
          loading={lazyload == true ? "lazy" : "eager"}
          fetchpriority={priority}
          className={classNames}
          alt={image.altText}
          width={imageProportions.width ? imageProportions.width : undefined}
          height={imageProportions.height ? imageProportions.height : undefined}
          src={image.imageFallback}
        />
      </picture>
    ) : (
      <></>
    )
  },
  ModernImagePicture: (props: {
    pictureKey: string,
    sources: ImageSourcesProps['sources']
    image: ImageSizesProps['image']
    imageProportions: ImageProportionsProps
    lazyload: boolean
    priority: 'high' | 'low' | undefined
    emptyOnMobile?: boolean
    classNames?: string
    pictureClassNames?: string
  }) => {
    const { pictureKey, classNames, pictureClassNames, image, imageProportions, lazyload, priority, sources, emptyOnMobile } = props

    const baseSrc = (emptyOnMobile != undefined && emptyOnMobile == true) ? image?.originalImageUrl : image?.imageFallback ;

    return (
      <picture className={pictureClassNames}>
        {sources &&
          sources.map(
            (src, index) =>
              src.image && (
                <React.Fragment key={`source-${pictureKey}-${index}`}>
                  <source
                    key={`ip-${pictureKey}-${index}`}
                    media={src.imageProportions.media}
                    srcSet={src.image.imageSizes.length > 0
                      ? src.image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                      : undefined
                    }
                    sizes={src.imageProportions.sizes ? src.imageProportions.sizes : '100vw'}
                    type='image/webp'
                  />
                  <source
                    key={`ip-${pictureKey}-${index}-fb`}
                    media={src.imageProportions.media}
                    srcSet={`${src.image.imageFallback} ${src.image.imageSizes[0].width}w`}
                    sizes={src.imageProportions.sizes ? src.imageProportions.sizes : '100vw'}
                  />
                </React.Fragment>
              )
          )}
        {image && (
          <>
            {(emptyOnMobile != undefined && emptyOnMobile == true) ? <></> :
              <source
                srcSet={image.imageSizes.length > 0
                  ? image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                  : undefined
                }
                sizes={imageProportions.sizes ? imageProportions.sizes : '100vw'}
                type='image/webp'
              />
            }
            <img
              loading={lazyload == true ? "lazy" : "eager"}
              fetchpriority={priority}
              className={classNames}
              alt={image.altText}
              width={imageProportions.width ? imageProportions.width : undefined}
              height={imageProportions.height ? imageProportions.height : undefined}
              src={baseSrc}
            />
          </>
        )}
      </picture>
    )
  },
  ImagePicture: (props: {
    pictureKey: string,
    sources: ImageSourcesProps['sources']
    image: ImageSizesProps['image']
    imageProportions: ImageProportionsProps
    lazyload: boolean
    classNames?: string
    placeholder?: string
  }) => {
    const { pictureKey, classNames, image, imageProportions, lazyload, sources } = props
    const placeholder = props.placeholder ? props.placeholder : 'images/spa/empty.png'
    return (
      <picture>
        {sources &&
          sources.map(
            (src, index) =>
              src.image && (
                <source
                  key={`ip-${pictureKey}-${index}`}
                  media={src.imageProportions.media}
                  srcSet={
                    lazyload == false && src.image.imageSizes.length > 0
                      ? src.image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                      : undefined
                  }
                  data-srcset={
                    lazyload == true && src.image.imageSizes.length > 0
                      ? src.image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                      : undefined
                  }
                />
              )
          )}
        {image && (
          <>
            <source
              srcSet={
                lazyload == false && image.imageSizes.length > 0
                  ? image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                  : undefined
              }
              data-srcset={
                lazyload == true && image.imageSizes.length > 0
                  ? image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                  : undefined
              }
            />
            <img
              className={classNames}
              alt={image.altText}
              width={imageProportions.width ? imageProportions.width : undefined}
              height={imageProportions.height ? imageProportions.height : undefined}
              src={lazyload == false ? image.imageSizes[0]?.url : placeholder}
              srcSet={
                lazyload == false && image.imageSizes.length > 0
                  ? image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                  : undefined
              }
              sizes={
                lazyload == false && image.imageSizes.length > 0
                  ? imageProportions.sizes
                    ? imageProportions.sizes
                    : '100vw'
                  : undefined
              }
              data-src={lazyload == true ? image.imageSizes[0]?.url : undefined}
              data-srcset={
                lazyload == true && image.imageSizes.length > 0
                  ? image.imageSizes.map(({ url, width }) => `${url} ${width}w`).join(', ')
                  : undefined
              }
              data-sizes={
                lazyload == true && image.imageSizes.length > 0
                  ? imageProportions.sizes
                    ? imageProportions.sizes
                    : 'auto'
                  : undefined
              }
            />
          </>
        )}
      </picture>
    )
  },
}
